import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Grid,
    Stack,
    Typography,
    TextField,
    Button,
    Alert,
    Avatar,
    Chip,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import {
    Visibility,
    VisibilityOff,
    Lock,
    Person,
    SportsEsports,
    OpenInNew,
} from "@mui/icons-material";
import { useReferentialContext } from "../../Context";
import { NeonCard, NeonCardHeader, NeonCardContent } from "../../NeonComponents/NeonCards";
import {
    NeonDialog,
    NeonDialogTitle,
    NeonDialogContent,
    NeonDialogActions,
} from "../../NeonComponents/NeonDialog";
import { getNeonColor } from "../../theme";
import { useTheme } from "@mui/system";
import { buildRsoAuthUrl } from "./rsoConfig";

// ---------------------------------------------------------------------------
// Profile page
// ---------------------------------------------------------------------------

export default function ProfilePage() {
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    // Riot account state
    const [riotLoading, setRiotLoading] = useState(false);
    const [riotSuccess, setRiotSuccess] = useState<string | null>(null);
    const [riotError, setRiotError] = useState<string | null>(null);
    const [linkedAccount, setLinkedAccount] = useState<{
        gamename: string | null;
        tagline: string | null;
    } | null>(null);

    // Password dialog state
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    // Whether RSO is configured (client ID has been set)
    const rsoConfigured = true;

    // ------------------------------------------------------------------
    // Fetch existing linked Riot account on mount
    // ------------------------------------------------------------------

    useEffect(() => {
        context.reckonApi.users
            .get({ id: String(context.user.user_id) })
            .then((user) => {
                if (user.riot_puuid) {
                    setLinkedAccount({
                        gamename: user.riot_gamename ?? null,
                        tagline: user.riot_tagline ?? null,
                    });
                }
            })
            .catch(() => {});
    }, []);

    // ------------------------------------------------------------------
    // Riot RSO popup OAuth2 flow
    // ------------------------------------------------------------------

    const handleRiotMessage = useCallback((event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== "riot-auth-callback") return;

        setRiotLoading(false);
        if (event.data.success) {
            setLinkedAccount({
                gamename: event.data.account?.gamename ?? null,
                tagline: event.data.account?.tagline ?? null,
            });
            setRiotSuccess(
                event.data.account?.gamename
                    ? `Linked as ${event.data.account.gamename}#${event.data.account.tagline}`
                    : "Riot account linked successfully!"
            );
            setRiotError(null);
        } else {
            setRiotError(event.data.error || "Failed to link Riot account.");
            setRiotSuccess(null);
        }
    }, []);

    useEffect(() => {
        window.addEventListener("message", handleRiotMessage);
        return () => window.removeEventListener("message", handleRiotMessage);
    }, [handleRiotMessage]);

    const handleLinkRiot = () => {
        setRiotError(null);
        setRiotSuccess(null);
        setRiotLoading(true);

        const width = 520;
        const height = 680;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
            buildRsoAuthUrl(),
            "riot-auth",
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );

        // If the popup was blocked or closed before completing, reset loading
        if (!popup) {
            setRiotLoading(false);
            setRiotError("Popup was blocked. Please allow popups for this site.");
            return;
        }

        const pollTimer = setInterval(() => {
            if (popup.closed) {
                clearInterval(pollTimer);
                setRiotLoading(false);
            }
        }, 500);
    };

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    return (
        <Box sx={{ width: "100%", padding: 4, maxWidth: "900px", margin: "0 auto" }}>
            {/* Profile Header */}
            <Stack direction="row" spacing={3} sx={{ mb: 4, alignItems: "center" }}>
                <Avatar
                    sx={{
                        width: 72,
                        height: 72,
                        bgcolor: `${neonColor}30`,
                        border: `2px solid ${neonColor}`,
                        boxShadow: `0 0 20px ${neonColor}40`,
                    }}
                >
                    <Person sx={{ fontSize: 40, color: neonColor }} />
                </Avatar>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {context.user.username}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip
                            label={context.user.team}
                            size="small"
                            sx={{
                                bgcolor: `${neonColor}20`,
                                color: neonColor,
                                borderColor: `${neonColor}40`,
                                border: "1px solid",
                            }}
                        />
                        <Chip
                            label={context.user.organization}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: `${neonColor}40`, color: "text.secondary" }}
                        />
                    </Stack>
                </Box>
            </Stack>

            <Grid container spacing={3}>
                {/* ---------------------------------------------------------- */}
                {/* Riot Account Section                                       */}
                {/* ---------------------------------------------------------- */}
                <Grid size={{ xs: 12 }}>
                    <NeonCard>
                        <NeonCardHeader
                            avatar={
                                <SportsEsports
                                    sx={{
                                        color: neonColor,
                                        filter: `drop-shadow(0 0 6px ${neonColor})`,
                                    }}
                                />
                            }
                            title="Riot Account"
                            subheader="Link your Riot Games account via Riot Sign On (RSO)"
                        />
                        <NeonCardContent>
                            <Stack spacing={2.5}>
                                {/* Linked account info */}
                                {linkedAccount && (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Currently linked:{" "}
                                        <strong>
                                            {linkedAccount.gamename}#{linkedAccount.tagline}
                                        </strong>
                                    </Alert>
                                )}

                                {riotSuccess && (
                                    <Alert
                                        severity="success"
                                        onClose={() => setRiotSuccess(null)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {riotSuccess}
                                    </Alert>
                                )}
                                {riotError && (
                                    <Alert
                                        severity="error"
                                        onClose={() => setRiotError(null)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {riotError}
                                    </Alert>
                                )}

                                {!rsoConfigured && (
                                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                        RSO is not configured yet. Set your{" "}
                                        <code>RSO_CLIENT_ID</code> in{" "}
                                        <code>src/pages/Profile/rsoConfig.ts</code> to enable
                                        Riot account linking.
                                    </Alert>
                                )}

                                <Typography variant="body2" color="text.secondary">
                                    Clicking the button below will open a Riot Games login window.
                                    After you sign in, your Riot account will be linked
                                    automatically. This enables match history and private game
                                    tracking.
                                </Typography>

                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={handleLinkRiot}
                                        disabled={riotLoading || !rsoConfigured}
                                        startIcon={
                                            riotLoading ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <OpenInNew />
                                            )
                                        }
                                        sx={{
                                            bgcolor: "#d13639",
                                            color: "#fff",
                                            fontWeight: 700,
                                            textTransform: "none",
                                            px: 3,
                                            "&:hover": {
                                                bgcolor: "#b72d30",
                                                boxShadow: "0 0 15px #d1363960",
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: "#d1363640",
                                                color: "#ffffff80",
                                            },
                                        }}
                                    >
                                        {riotLoading
                                            ? "Waiting for Riot login..."
                                            : linkedAccount
                                              ? "Re-link Riot Account"
                                              : "Sign in with Riot Games"}
                                    </Button>
                                </Box>
                            </Stack>
                        </NeonCardContent>
                    </NeonCard>
                </Grid>

                {/* ---------------------------------------------------------- */}
                {/* Security Section                                           */}
                {/* ---------------------------------------------------------- */}
                <Grid size={{ xs: 12 }}>
                    <NeonCard>
                        <NeonCardHeader
                            avatar={
                                <Lock
                                    sx={{
                                        color: neonColor,
                                        filter: `drop-shadow(0 0 6px ${neonColor})`,
                                    }}
                                />
                            }
                            title="Security"
                            subheader="Manage your account credentials"
                        />
                        <NeonCardContent>
                            <Stack
                                direction="row"
                                sx={{ alignItems: "center", justifyContent: "space-between" }}
                            >
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Password
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Change your account password
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    onClick={() => setPasswordDialogOpen(true)}
                                    startIcon={<Lock />}
                                    sx={{
                                        borderColor: `${neonColor}60`,
                                        color: neonColor,
                                        textTransform: "none",
                                        "&:hover": {
                                            borderColor: neonColor,
                                            bgcolor: `${neonColor}10`,
                                        },
                                    }}
                                >
                                    Change Password
                                </Button>
                            </Stack>
                        </NeonCardContent>
                    </NeonCard>
                </Grid>
            </Grid>

            {/* Password Change Dialog */}
            <ChangePasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
            />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Change Password Dialog
// ---------------------------------------------------------------------------

function ChangePasswordDialog(props: { open: boolean; onClose: () => void }) {
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowOld(false);
        setShowNew(false);
        setShowConfirm(false);
        setSuccess(null);
        setError(null);
    };

    const handleClose = () => {
        reset();
        props.onClose();
    };

    const handleSubmit = async () => {
        setError(null);
        setSuccess(null);

        if (!oldPassword || !newPassword || !confirmPassword) {
            setError("All fields are required.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }
        if (newPassword.length < 8) {
            setError("New password must be at least 8 characters.");
            return;
        }

        setLoading(true);
        try {
            await context.reckonApi.changePassword.post({
                change_password: {
                    old_password: oldPassword,
                    new_password: newPassword,
                },
            });
            setSuccess("Password changed successfully.");
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (e: any) {
            setError(
                e?.response?.statusText ||
                    "Failed to change password. Please check your current password."
            );
        } finally {
            setLoading(false);
        }
    };

    const textFieldSx = {
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: `${neonColor}40` },
            "&:hover fieldset": { borderColor: `${neonColor}80` },
            "&.Mui-focused fieldset": {
                borderColor: neonColor,
                boxShadow: `0 0 8px ${neonColor}30`,
            },
        },
        "& .MuiInputLabel-root.Mui-focused": { color: neonColor },
    };

    const passwordField = (
        label: string,
        value: string,
        setValue: (v: string) => void,
        show: boolean,
        setShow: (v: boolean) => void
    ) => (
        <TextField
            label={label}
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            size="small"
            sx={textFieldSx}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShow(!show)} edge="end" size="small">
                                {show ? (
                                    <VisibilityOff fontSize="small" />
                                ) : (
                                    <Visibility fontSize="small" />
                                )}
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );

    return (
        <NeonDialog open={props.open} onClose={handleClose} maxWidth="xs" fullWidth>
            <NeonDialogTitle>Change Password</NeonDialogTitle>
            <NeonDialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {success && (
                        <Alert severity="success" sx={{ borderRadius: 2 }}>
                            {success}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {passwordField("Current Password", oldPassword, setOldPassword, showOld, setShowOld)}
                    {passwordField("New Password", newPassword, setNewPassword, showNew, setShowNew)}
                    {passwordField(
                        "Confirm New Password",
                        confirmPassword,
                        setConfirmPassword,
                        showConfirm,
                        setShowConfirm
                    )}
                </Stack>
            </NeonDialogContent>
            <NeonDialogActions>
                <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <Lock />}
                    sx={{
                        bgcolor: neonColor,
                        textTransform: "none",
                        "&:hover": {
                            bgcolor: neonColor,
                            filter: "brightness(1.2)",
                            boxShadow: `0 0 15px ${neonColor}60`,
                        },
                    }}
                >
                    {loading ? "Updating..." : "Update Password"}
                </Button>
            </NeonDialogActions>
        </NeonDialog>
    );
}
